<template>
  <BaseModal
    :modalTitle="modalTitle"
    contentPadding="none">
    <div
      class="flex items-center justify-end gap-3 border-b border-border-subtle px-6 py-3">
      <span
        v-if="pdfError"
        class="text-feedback-error text-sm">
        {{ pdfError }}
      </span>
      <UiBaseButton
        :text="$t('W: Download PDF')"
        :isLoading="isGeneratingPdf"
        @click="generatePdf">
        <template #icon-left>
          <IconLucideDownload />
        </template>
      </UiBaseButton>
    </div>
    <div class="flex justify-center bg-surface-sunken px-6 pt-4 pb-6">
      <div
        ref="previewContainer"
        class="overflow-hidden"
        style="width: 684px">
        <iframe
          ref="previewIframe"
          :srcdoc="previewHtml"
          scrolling="no"
          style="
            width: 854px;
            height: 100px;
            transform: scale(0.8);
            transform-origin: top left;
            border: 0;
            display: block;
          "
          title="Report preview"
          @load="onIframeLoad" />
      </div>
    </div>
  </BaseModal>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import {
  renderLabBase1,
  LAB_BASE_1_VARIABLES,
  formatReportAnalysesList,
  resolveReportDateVariables,
} from '@team-milva/lab-pdfs'
import { labBase1TFor } from '@/globals/typescript/utils/labBase1T'
import { tIn } from '@/locales'
import { buildReportPdfFilename } from '@team-milva/lab-utils/filename'
import { getTextObjValue } from '@team-milva/lab-models/textObj'
import { PhoneNumber } from '@team-milva/utils/phone'
import { useReportTemplateStore } from '@/stores/reportTemplateStore'
import { useAccountStore } from '@/stores/accountStore'
import { useAppStore } from '@/stores/appStore'
import { useAnalysisStore } from '@/stores/analysisStore'
import { useAnalysisTestStore } from '@/stores/analysisTestStore'
import { useTextListStore } from '@/stores/textListStore'
import { useOrderStore } from '@/stores/orderStore'
import { useCustomerAccountStore } from '@/stores/customerAccountStore'
import { useInsuranceCustomerAccountStore } from '@/stores/insuranceCustomerAccountStore'
import {
  resolveOrderOwnerName,
  type IOrderOwnerNameLookups,
} from '@/globals/typescript/utils/orderOwner'
import { useCustomerUserStore } from '@/stores/customerUserStore'
import { useReportDescriptionItemStore } from '@/stores/reportDescriptionItemStore'
import { useReportDescriptionStore } from '@/stores/reportDescriptionStore'
import { HOST } from '@/globals/typescript/utils/host'
import { cloudinaryTransformations } from '@team-milva/lab-constants/cloudinary'
import { resolveOrderResultsForReport } from '@/globals/typescript/utils/resolveOrderResultsForReport'
import type { IAnalysisResult } from '@team-milva/lab-models/resources'
import {
  resolveSamplesExplained,
  toSamplesExplainedSources,
} from '@team-milva/lab-models/resources'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { db } from '@/firebase/init'
import { getCurrentUserBearer } from '@/globals/typescript/utils/api/getCurrentUserBearer'
import { useToast } from '@/globals/typescript/utils/toast'
import { reportError } from '@/globals/typescript/utils/reportError'

const ORDER_VARIABLES = LAB_BASE_1_VARIABLES.filter((x) => x.tier === 'order')

export default {
  name: 'OrderReportPreviewModal',

  props: {
    reportTemplateID: { type: String, required: true },
    tatGroupID: { type: String, required: true },
    orderID: { type: String, required: true },
  },

  data() {
    return {
      orderResults: [] as IAnalysisResult[],
      isGeneratingPdf: false,
      pdfError: '',
    }
  },

  computed: {
    ...mapState(useReportTemplateStore, ['allReportTemplates']),
    ...mapState(useAccountStore, ['labAccount', 'allLabAccountUsers']),
    ...mapState(useAppStore, ['currentLocale']),
    ...mapState(useAnalysisStore, ['allAnalyses']),
    ...mapState(useAnalysisTestStore, ['allAnalysisTests']),
    ...mapState(useTextListStore, ['systemLists']),
    ...mapState(useOrderStore, ['allOrders']),
    ...mapState(useCustomerAccountStore, ['allCustomerAccounts']),
    ...mapState(useCustomerUserStore, ['customerUsers']),
    ...mapState(useInsuranceCustomerAccountStore, ['allInsuranceCustomerAccounts']),
    ...mapState(useReportDescriptionItemStore, ['allReportDescriptionItems']),
    ...mapState(useReportDescriptionStore, ['allReportDescriptions']),

    order() {
      return this.allOrders.find((x) => x.id === this.orderID)
    },

    orderOwnerLookups(): IOrderOwnerNameLookups {
      return {
        customerAccountsById: Object.fromEntries(
          this.allCustomerAccounts.map((x) => [x.id, x])
        ),
        insuranceCustomerAccountsById: Object.fromEntries(
          this.allInsuranceCustomerAccounts.map((x) => [x.id, x])
        ),
      }
    },

    reportTemplate() {
      return this.allReportTemplates.find((x) => x.id === this.reportTemplateID)
    },

    /**
     * The report header's addressee and case details (MIL-373). Mirrors the
     * same block in generateReportPdfsForTatGroup.ts — the preview and the
     * generated PDF have to agree, so a change to one belongs in both.
     */
    recipient() {
      const order = this.order
      if (!order) return null
      const customerName = resolveOrderOwnerName(order, this.orderOwnerLookups) ?? ''
      // Business and private customer users share one collection and one store,
      // so a single lookup covers both. A private order has no customer account,
      // which is why the company name can legitimately come back empty. An insurance
      // customer's own order is addressed to the insurance customer (MIL-402).
      const contactUserID = order.privateUserID || order.customerUserID
      const contact = this.customerUsers.find((x) => x.id === contactUserID)
      return {
        customerName,
        contactName: [contact?.firstName, contact?.lastName]
          .filter(Boolean)
          .join(' '),
        contactPhone: contact?.phone?.number
          ? new PhoneNumber(contact.phone).formattedInternational
          : null,
        contactEmail: contact?.email ?? null,
        siteAddressLines: order.address
          ? [
              order.address.addressLine,
              `${order.address.postalCode} ${order.address.city}`,
            ]
          : [],
        caseNumber: order.caseNumber || null,
        insurancePartnerName: order.insurancePartnerID
          ? (this.allInsuranceCustomerAccounts.find((x) => x.id === order.insurancePartnerID)
              ?.name ?? null)
          : null,
        insuranceNumber: order.insuranceNumber ?? null,
      }
    },

    // Everything the customer reads renders in the order's locale, never the
    // staff member's UI locale. Must stay in step with the `reportLocale`
    // resolution in generateReportPdfsForTatGroup.ts — if the two disagree, the
    // preview shows something other than what is sent out (MIL-268).
    reportLocale(): string {
      return this.order?.reportLocale || this.currentLocale
    },

    // Same builder as the emailed attachment and the Deliveries download, with the
    // report name tagged as a draft in the order's report locale (not the staff UI
    // locale), so a downloaded preview is named like the real report but cannot be
    // mistaken for it.
    pdfFilename(): string {
      const reportName = this.reportTemplate?.getDisplayTitle(this.reportLocale) ?? ''
      const previewTag = tIn(this.reportLocale, 'report.file-preview-tag')
      return buildReportPdfFilename({
        orderNumber: this.order?.orderNumber ?? '',
        address: this.order?.address,
        reportName: `${reportName} (${previewTag})`,
      })
    },

    modalTitle(): string {
      if (!this.reportTemplate) return this.$t('W: Forhåndsvisning')
      return `${this.reportTemplate.getTitle(this.currentLocale)} — ${this.$t('W: Forhåndsvisning')}`
    },

    resolvedResults() {
      if (!this.order) return []
      return resolveOrderResultsForReport({
        order: this.order,
        reportTemplateID: this.reportTemplateID,
        tatGroupID: this.tatGroupID,
        results: this.orderResults,
        allAnalyses: this.allAnalyses,
        allAnalysisTests: this.allAnalysisTests,
        allReportDescriptionItems: this.allReportDescriptionItems,
        systemLists: this.systemLists,
        locale: this.reportLocale,
      })
    },

    leftSignatory() {
      const userID =
        this.order?.reportResponsibleUserAssignments?.[
          `${this.tatGroupID}__${this.reportTemplateID}`
        ] ?? null
      if (!userID) return null
      const user = this.allLabAccountUsers.find((x) => x.id === userID)
      if (!user) return null
      const signaturePath = user.images?.signature?.path ?? null
      return {
        name: user.fullName,
        title: 'Laborant',
        signatureImageUrl: signaturePath
          ? `${HOST.cloudinary}/${signaturePath}?tx=${cloudinaryTransformations.SIGNATURE_PDF}`
          : undefined,
      }
    },

    rightSignatory() {
      const userID =
        this.labAccount?.reportSettings?.laboratoryManagerUserID ?? null
      if (!userID) return null
      const user = this.allLabAccountUsers.find((x) => x.id === userID)
      if (!user) return null
      const signaturePath = user.images?.signature?.path ?? null
      return {
        name: user.fullName,
        title: 'Laboratorieleder',
        signatureImageUrl: signaturePath
          ? `${HOST.cloudinary}/${signaturePath}?tx=${cloudinaryTransformations.SIGNATURE_PDF}`
          : undefined,
      }
    },

    manualDescriptions(): Array<{ title: string; text: string }> {
      if (!this.order) return []
      const reportLocale = this.reportLocale
      const selectionKey = `${this.tatGroupID}__${this.reportTemplateID}`
      const selectedItemIDs =
        (this.order.reportDescriptionSelections ?? {})[selectionKey] ?? []
      return selectedItemIDs
        .map((itemID) => {
          const item = this.allReportDescriptionItems.find((x) => x.id === itemID)
          if (!item) return null
          const title = item.getTitle(reportLocale)
          const text = item.getText(reportLocale)
          if (!title && !text) return null
          return { title, text }
        })
        .filter((x): x is { title: string; text: string } => x !== null)
    },

    // Per-sample explanations — the same shared resolution the generated PDF uses
    // (lab-models), so what is previewed here is what the customer receives.
    samplesExplained() {
      if (!this.order) return []
      const hasBlock = this.reportTemplate?.blocks.some(
        (block) => block.type === 'samples-explained'
      )
      if (!hasBlock) return []
      return resolveSamplesExplained({
        samples: toSamplesExplainedSources(this.resolvedResults),
        analyses: this.allAnalyses,
        reportDescriptionItems: this.allReportDescriptionItems,
        reportDescriptions: this.allReportDescriptions,
        overridesBySampleID:
          this.order.reportSampleDescriptionOverrides?.[
            `${this.tatGroupID}__${this.reportTemplateID}`
          ],
        labAccountID: this.labAccount?.id ?? '',
        locale: this.reportLocale,
      })
    },

    previewHtml(): string {
      const lab = this.labAccount
      const reportLocale = this.reportLocale
      const logoImage = lab?.images.logo ?? null
      const logoUrl = logoImage
        ? `${HOST.cloudinary}/${logoImage.path}?tx=${cloudinaryTransformations.LOGO_PDF}`
        : null

      const partnerLogoImage = this.reportTemplate?.partnerLogo ?? null
      const partnerLogoUrl = partnerLogoImage
        ? `${HOST.cloudinary}/${partnerLogoImage.path}?tx=${cloudinaryTransformations.PARTNER_LOGO_PDF}`
        : null
      const previewDate = new Intl.DateTimeFormat(reportLocale, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(new Date())
      const confirmedAt = this.order?.timestamps.confirmedAt ?? null
      const confirmedDateDefault = confirmedAt
        ? new Intl.DateTimeFormat(reportLocale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }).format(confirmedAt)
        : previewDate
      const confirmedDateTimeDefault = confirmedAt
        ? new Intl.DateTimeFormat(reportLocale, {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          }).format(confirmedAt)
        : previewDate
      const customerName = this.order
        ? (resolveOrderOwnerName(this.order, this.orderOwnerLookups) ?? '')
        : ''
      const templateAnalysisIDs = new Set(
        Object.entries(this.order?.reportTemplateAssignments ?? {})
          .filter(
            ([key, templateID]) =>
              templateID === this.reportTemplateID &&
              key.startsWith(`${this.tatGroupID}__`)
          )
          .map(([key]) => key.split('__')[1])
      )
      const autoDescriptions: Array<{ name: string; text: string }> = []
      for (const id of templateAnalysisIDs) {
        const analysis = this.allAnalyses.find((x) => x.id === id)
        if (!analysis?.autoDescription?.enabled) continue
        const text = getTextObjValue(analysis.autoDescription.text, reportLocale)
        if (!text) continue
        const name = analysis.getName(reportLocale)
        autoDescriptions.push({ name, text })
      }
      const activeSamples = (this.order?.samples ?? []).filter(
        (sample) =>
          sample.status !== 'cancelled' &&
          sample.tatGroupID === this.tatGroupID &&
          sample.analyses.some(
            (a) =>
              a.status !== 'cancelled' && templateAnalysisIDs.has(a.analysisID)
          )
      )
      const phone = lab?.primaryContact.phone
      const stored = this.reportTemplate?.previewVariables ?? {}
      // The dates the report's samples were received and analysed — the same function lab-cloud
      // uses for the PDF, and (like caseNumber) never a saved preview value, so the two match.
      const { receivedDate, analysedDate, receivedDateText, analysedDateText } =
        resolveReportDateVariables({
          samples: this.order?.samples ?? [],
          tatGroupID: this.tatGroupID,
          templateAnalysisIDs: [...templateAnalysisIDs].filter(
            (id): id is string => id !== undefined
          ),
          locale: reportLocale,
        })

      const variables: Record<string, string> = {
        labName: lab?.companyDetails.name ?? '',
        labPhone: phone?.number ? new PhoneNumber(phone).formatted : '',
        labAddressLine: lab?.address.addressLine ?? '',
        labPostalCode: lab?.address.postalCode ?? '',
        labCity: lab?.address.city ?? '',
      }
      for (const variable of ORDER_VARIABLES) {
        if (variable.key === 'customerName') {
          variables[variable.key] = customerName
        } else if (variable.key === 'sampleCount') {
          variables[variable.key] =
            stored[variable.key] || String(activeSamples.length)
        } else if (variable.key === 'analysesCount') {
          const count = activeSamples.reduce(
            (sum, sample) =>
              sum +
              sample.analyses.filter(
                (a) =>
                  a.status !== 'cancelled' &&
                  templateAnalysisIDs.has(a.analysisID)
              ).length,
            0
          )
          variables[variable.key] = stored[variable.key] || String(count)
        } else if (variable.key === 'orderNumber') {
          variables[variable.key] = this.order
            ? `#${this.order.orderNumber}`
            : (variable.previewDefault ?? '')
        } else if (variable.key === 'caseNumber') {
          // Same reason as report.caseNumber below: previewDefault ('123-ABC') is
          // for the order-less template preview. Here it would print a case number
          // this order does not have, and lab-cloud's own generator resolves the
          // variable to '' — so the preview would not match the PDF (MIL-369).
          variables[variable.key] = this.order?.caseNumber || ''
        } else if (variable.key === 'addressLine') {
          const address = this.order?.address
          variables[variable.key] = address
            ? `${address.addressLine}, ${address.postalCode} ${address.city}`
            : variable.previewDefault || ''
        } else if (variable.key === 'receivedDate') {
          variables[variable.key] = receivedDate
        } else if (variable.key === 'analysedDate') {
          variables[variable.key] = analysedDate
        } else if (variable.key === 'receivedDateText') {
          variables[variable.key] = receivedDateText
        } else if (variable.key === 'analysedDateText') {
          variables[variable.key] = analysedDateText
        } else if (variable.key === 'analysesList') {
          const assignedAnalysisIDs = Object.entries(
            this.order?.reportTemplateAssignments ?? {}
          )
            .filter(
              ([key, templateID]) =>
                templateID === this.reportTemplateID &&
                key.startsWith(`${this.tatGroupID}__`)
            )
            .map(([key]) => key.split('__')[1])
          const names = assignedAnalysisIDs
            .map((id) => {
              const analysis = this.allAnalyses.find((x) => x.id === id)
              return analysis ? analysis.getName(reportLocale) : null
            })
            .filter((name): name is string => name !== null)
          // The same joining as the PDF, in the report's language.
          variables[variable.key] = formatReportAnalysesList(names, reportLocale)
        } else {
          const fallback =
            variable.key === 'confirmedDate'
              ? confirmedDateDefault
              : variable.key === 'confirmedDateTime'
                ? confirmedDateTimeDefault
                : (variable.previewDefault ?? '')
          variables[variable.key] = stored[variable.key] || fallback
        }
      }

      const reportPhone = lab?.reportSettings?.phone
      const reportPhoneFormatted = reportPhone?.number
        ? new PhoneNumber(reportPhone).formattedInternational
        : null

      const reportT = labBase1TFor(reportLocale)
      return renderLabBase1({
        locale: reportLocale,
        t: reportT,
        lab: {
          name: lab?.companyDetails.name ?? '',
          logoUrl,
          partnerLogoUrl,
          addressLine: lab?.address.addressLine ?? '',
          postalCode: lab?.address.postalCode ?? '',
          city: lab?.address.city ?? '',
          countryName:
            new Intl.DisplayNames([reportLocale], { type: 'region' }).of('DK') ??
            'Denmark',
          phone: reportPhoneFormatted,
          email: lab?.reportSettings?.email ?? null,
        },
        report: {
          date: confirmedAt
            ? new Intl.DateTimeFormat(reportLocale, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              }).format(confirmedAt)
            : previewDate,
          orderNumber: this.order ? `#${this.order.orderNumber}` : '',
        },
        recipient: this.recipient,
        blocks: (
          this.reportTemplate?.blocks ?? [{ id: 'auto', type: 'auto' as const }]
        ).map((block) => ({
          id: block.id,
          type: block.type,
          body:
            block.type === 'text'
              ? getTextObjValue(block.body, reportLocale)
              : '',
        })),
        variables,
        autoDescriptions,
        manualDescriptions: this.manualDescriptions,
        samplesExplained: this.samplesExplained,
        orderDescription: (() => {
          const raw = (this.order?.reportOrderDescriptions ?? {})[`${this.tatGroupID}__${this.reportTemplateID}`]
          return raw?.replace(/<[^>]*>/g, '').trim() ? raw : undefined
        })(),
        results: this.resolvedResults,
        pageEnding: {
          contactText: reportT('report.contact'),
          closingText: reportT('report.closing'),
          labName: lab?.companyDetails.name ?? '',
          leftSignatory: this.leftSignatory,
          rightSignatory: (() => {
            const responsibleUserID =
              this.order?.reportResponsibleUserAssignments?.[
                `${this.tatGroupID}__${this.reportTemplateID}`
              ] ?? null
            const laboratoryManagerUserID =
              this.labAccount?.reportSettings?.laboratoryManagerUserID ?? null
            if (
              responsibleUserID &&
              laboratoryManagerUserID &&
              responsibleUserID === laboratoryManagerUserID
            ) {
              return null
            }
            return this.rightSignatory
          })(),
        },
      })
    },
  },

  methods: {
    /** Resize the iframe and its scaled container once the pagination script
     *  inside the srcdoc iframe has finished rearranging page elements.
     *  postMessage is used because the inner window.load fires before the
     *  outer @load event, but DOM mutations from the script may still be
     *  mid-flight when the host measures scrollHeight. */
    onPdfPaginated(event: MessageEvent): void {
      if (!event.data || event.data.type !== 'lab-pdf-paginated') return
      const iframe = this.$refs.previewIframe as HTMLIFrameElement | undefined
      if (!iframe || event.source !== iframe.contentWindow) return
      const scrollHeight = event.data.scrollHeight as number
      iframe.style.height = scrollHeight + 'px'
      const container = this.$refs.previewContainer as HTMLElement | undefined
      if (container) {
        container.style.height = Math.round(scrollHeight * 0.8) + 'px'
      }
    },

    onIframeLoad(): void {
      const iframe = this.$refs.previewIframe as HTMLIFrameElement | undefined
      if (!iframe?.contentWindow?.document?.body) return
      const scrollHeight = iframe.contentWindow.document.body.scrollHeight
      iframe.style.height = scrollHeight + 'px'
      const container = this.$refs.previewContainer as HTMLElement | undefined
      if (container) {
        container.style.height = Math.round(scrollHeight * 0.8) + 'px'
      }
    },

    async generatePdf(): Promise<void> {
      this.isGeneratingPdf = true
      this.pdfError = ''
      try {
        const labAccountID = this.labAccount?.id ?? ''
        const token = await getCurrentUserBearer()
        const response = await fetch(
          `${HOST.functions}/management/v1/lab-accounts/${labAccountID}/report-templates/${this.reportTemplateID}/generate-pdf`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            // The order and delivery let the backend append this report's Bilag to the download,
            // so the draft matches the file that will be sent. The preview above stays report-only.
            body: JSON.stringify({
              html: this.previewHtml,
              orderID: this.orderID,
              tatGroupID: this.tatGroupID,
            }),
          }
        )
        if (!response.ok) throw new Error(`${response.status}`)
        const blob = await response.blob()
        const url = URL.createObjectURL(blob)
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = this.pdfFilename
        anchor.click()
        URL.revokeObjectURL(url)
        useToast().success(this.$t('W: PDF downloadet'))
      } catch {
        this.pdfError = this.$t('W: PDF kunne ikke genereres')
        useToast().error(this.$t('W: PDF kunne ikke genereres'))
      } finally {
        this.isGeneratingPdf = false
      }
    },
  },

  async created() {
    const labAccountID = this.labAccount?.id ?? ''
    if (!labAccountID || !this.orderID) return
    const descriptionItemStore = useReportDescriptionItemStore()
    // Isolated from the store load: this is a direct Firestore read with no
    // server-side trace, so a failure here must be reported (error-reporting.md)
    // rather than left to reject this created() hook silently.
    const fetchOrderResults = async () => {
      try {
        const snapshot = await getDocs(
          query(
            collection(db, 'Apps', '-lab-customer-', 'AnalysisResults'),
            where('labAccountID', '==', labAccountID),
            where('orderID', '==', this.orderID),
            where('activeState', '==', 'active')
          )
        )
        this.orderResults = snapshot.docs.map(
          (docSnap) => docSnap.data() as IAnalysisResult
        )
      } catch (error) {
        reportError(error, {
          operation: 'fetch-order-results-for-report-preview',
          extra: { orderID: this.orderID },
        })
      }
    }
    await Promise.all([
      fetchOrderResults(),
      descriptionItemStore.getReportDescriptionItems(),
      // The collections behind the items — a samples-explained section only auto-adds an item
      // whose collection still uses descriptions.
      useReportDescriptionStore().getReportDescriptions(),
      // The header names the order's contact person, who lives in this store.
      useCustomerUserStore().getCustomerUsers(),
      // ...and, on an insurance case, the partner being billed.
      useInsuranceCustomerAccountStore().getInsuranceCustomerAccounts(),
    ])
  },

  mounted() {
    window.addEventListener('message', this.onPdfPaginated)
  },

  beforeUnmount() {
    window.removeEventListener('message', this.onPdfPaginated)
  },
}
</script>

